import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { clientApiService, AdvanceItem, ProjectItem } from "../../services/client-api.service";

const STATUS_LABELS: Record<string, { label: string; className: string }> = {
  pending: { label: "Pendiente", className: "bg-yellow-100 text-yellow-700" },
  approved: { label: "Aprobado", className: "bg-green-100 text-green-700" },
  rejected: { label: "Rechazado", className: "bg-red-100 text-red-700" },
  draft: { label: "Borrador", className: "bg-slate-100 text-slate-700" },
  under_review: { label: "En revisión", className: "bg-blue-100 text-blue-700" },
};

function formatDate(t: number) {
  return new Date(t).toLocaleDateString("es-MX", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
}

export function AdvancesListPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const projectIdParam = searchParams.get("projectId") || "";

  const [advances, setAdvances] = useState<AdvanceItem[]>([]);
  const [projects, setProjects] = useState<ProjectItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [search, setSearch] = useState("");

  const load = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const [advancesData, projectsData] = await Promise.all([
        clientApiService.listAdvances(projectIdParam || undefined),
        clientApiService.listProjects(),
      ]);
      setAdvances(advancesData);
      setProjects(projectsData);
    } catch (err: any) {
      setError(err?.message || "Error al cargar avances");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectIdParam]);

  const filtered = useMemo(() => {
    return advances.filter((a) => {
      const matchesStatus = statusFilter === "all" || a.status === statusFilter;
      const term = search.trim().toLowerCase();
      const matchesSearch =
        !term ||
        a.projectName.toLowerCase().includes(term) ||
        (a.description || "").toLowerCase().includes(term) ||
        (a.contractorSpecialty || "").toLowerCase().includes(term);
      return matchesStatus && matchesSearch;
    });
  }, [advances, statusFilter, search]);

  const handleProjectChange = (id: string) => {
    const newParams = new URLSearchParams(searchParams);
    if (id) newParams.set("projectId", id);
    else newParams.delete("projectId");
    setSearchParams(newParams);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="text-2xl font-bold text-slate-900">Avances</h2>
          <p className="text-sm text-slate-500 mt-1">
            {advances.length} {advances.length === 1 ? "registro" : "registros"}
          </p>
        </div>
        <button
          onClick={load}
          disabled={isLoading}
          className="text-sm bg-white border border-slate-200 hover:bg-slate-50 text-slate-700 px-4 py-2 rounded-lg font-medium transition disabled:opacity-50"
        >
          {isLoading ? "Actualizando..." : "🔄 Actualizar"}
        </button>
      </div>

      {/* Toolbar */}
      <div className="flex flex-col sm:flex-row gap-3">
        <input
          type="text"
          placeholder="Buscar por proyecto, descripción o especialidad..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="flex-1 px-4 py-2.5 border border-slate-200 rounded-lg bg-white focus:border-primary focus:ring-2 focus:ring-primary/15 outline-none text-sm"
        />
        <select
          value={projectIdParam}
          onChange={(e) => handleProjectChange(e.target.value)}
          className="px-4 py-2.5 border border-slate-200 rounded-lg bg-white focus:border-primary outline-none text-sm"
        >
          <option value="">Todos los proyectos</option>
          {projects.map((p) => (
            <option key={p.id} value={p.id}>
              {p.name}
            </option>
          ))}
        </select>
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="px-4 py-2.5 border border-slate-200 rounded-lg bg-white focus:border-primary outline-none text-sm"
        >
          <option value="all">Todos los estados</option>
          <option value="pending">Pendiente</option>
          <option value="approved">Aprobado</option>
          <option value="rejected">Rechazado</option>
        </select>
      </div>

      {/* Content */}
      {isLoading && advances.length === 0 ? (
        <div className="flex items-center justify-center py-20">
          <div className="w-10 h-10 border-4 border-primary border-t-transparent rounded-full animate-spin" />
        </div>
      ) : error ? (
        <div className="bg-red-50 border border-red-200 text-red-700 rounded-xl p-4">
          {error}
          <button onClick={load} className="ml-3 underline font-medium">
            Reintentar
          </button>
        </div>
      ) : filtered.length === 0 ? (
        <div className="bg-white border border-slate-200 rounded-2xl p-12 text-center">
          <div className="text-5xl mb-3 opacity-40">📈</div>
          <h3 className="text-lg font-semibold text-slate-900">
            {advances.length === 0 ? "Sin avances aún" : "Sin resultados"}
          </h3>
          <p className="text-sm text-slate-500 mt-1 max-w-md mx-auto">
            {advances.length === 0
              ? "Los reportes semanales de avance de tus proyectos aparecerán aquí."
              : "Prueba con otros filtros o términos de búsqueda."}
          </p>
        </div>
      ) : (
        <ul className="space-y-3">
          {filtered.map((a) => (
            <AdvanceCard key={a.id} advance={a} />
          ))}
        </ul>
      )}
    </div>
  );
}

function AdvanceCard({ advance }: { advance: AdvanceItem }) {
  const status = STATUS_LABELS[advance.status] || {
    label: advance.status,
    className: "bg-slate-100 text-slate-700",
  };

  return (
    <li>
      <Link
        to={`/dashboard/advances/${advance.id}`}
        className="block bg-white rounded-2xl border border-slate-200 hover:border-primary/40 hover:shadow-md transition p-5"
      >
        <div className="flex items-start justify-between gap-4 flex-wrap">
          {/* Izquierda */}
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap mb-1.5">
              <span className="text-xs font-semibold bg-slate-100 text-slate-700 px-2 py-0.5 rounded">
                S{advance.weekNumber}/{advance.year}
              </span>
              {advance.contractorSpecialty && (
                <span className="text-xs font-semibold bg-blue-100 text-blue-700 px-2 py-0.5 rounded">
                  🏗️ {advance.contractorSpecialty}
                </span>
              )}
              <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${status.className}`}>
                {status.label}
              </span>
            </div>
            <p className="font-semibold text-slate-900 truncate">{advance.projectName}</p>
            {advance.description && (
              <p className="text-sm text-slate-600 mt-1 line-clamp-2">{advance.description}</p>
            )}
            <p className="text-xs text-slate-400 mt-2">Registrado: {formatDate(advance.createdAt)}</p>
          </div>

          {/* Derecha: porcentaje */}
          <div className="text-right shrink-0">
            <div className="text-2xl font-bold text-primary">
              +{advance.progressPercentage}%
            </div>
            <div className="text-xs text-slate-400">de avance</div>
          </div>
        </div>
      </Link>
    </li>
  );
}